import type { PropsWithChildren, ReactElement } from "react";
import { createContext, useEffect, useState } from "react";

import { useTranslations } from "@rootreeweb/react-translations";

import type { AppContext, AppProps, PouchType } from "../types.js";
import { resolvedWebsiteDataSchema } from "../types.js";

export const appContext = createContext<AppContext>({
	getLocalizedField: () => "",
	languageSuffix: "EN",
	links: {},
	pouches: [],
	rootPath: "",
});
/**
 * Provider for the app context with the dieline details and translations.
 *
 * @param props The host-provided app data.
 * @returns The provider.
 */
export function AppContextProvider(
	props: PropsWithChildren<AppProps>,
): ReactElement {
	const { setTranslations } = useTranslations();
	const [appState] = useState<AppContext>({
		...props,
		getLocalizedField: (data, field) =>
			String(
				data[(field + props.languageSuffix) as keyof typeof data] ||
					data[field as keyof typeof data],
			),
		pouches: resolvedWebsiteDataSchema
			.array()
			.parse(props.pouches.filter(shouldShow).map(fixIncomingPouch)),
	});
	useEffect(() => {
		setTranslations(props.translations);
	}, [props.translations, setTranslations]);
	return (
		<appContext.Provider value={appState}>{props.children}</appContext.Provider>
	);
}

/**
 * Provides transforms for pretty-ing up data.
 *
 * @param raw The data from the source.
 * @returns The transformed data
 */
function fixIncomingPouch(raw: PouchType): PouchType {
	raw.sizes.forEach((size) => {
		size.dielines.forEach((dieline) => {
			dieline.name = dieline.name.replace(/Cap O.$/, "Cap Off");
		});
	});
	return raw;
}

/**
 * Determines if a pouch should be shown in app.
 *
 * @param pouch The pouch to check.
 * @returns Whether the pouch should be shown.
 */
function shouldShow(pouch: PouchType): boolean {
	return !!(
		pouch.sizes.length && pouch.sizes.find((s) => s.dielines.length > 0)
	);
}
